import PasswordField from '../../components/PasswordInput/PasswordInput';
import Card from '../../components/Card/Card';
import { ERoutes, EQueryStringParam, EStaffAccountType } from '../../util/constants';
import LoginContext, { ELoginUiState } from '../../contexts/LoginContext';
import CoBranding from '../../components/CoBranding/CoBranding';
import { Link as RouterLink } from 'react-router-dom';
import styles from './LoginStaff.module.scss';
import common from '../../util/scss/common.module.scss';
import API from '../../api/api';
import GlobalContext from '../../contexts/GlobalContext';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, TextField, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageLayout from '../../layout/PageLayout/PageLayout';
import { getClientsStudioUrl } from '../../util/helpers';

const LoginStaff: FC = () => {
    const {
        language,
        coBranding,
        customerAccountId,
        staffAccountType,
        consumerLoginLink,
        webClientsBaseUrl,
        subscriberId,
        isStaffLogin,
    } = useContext(GlobalContext);
    const {
        username,
        setUsername,
        usernameIsValid,
        usernameValidationMessage,
        password,
        setPassword,
        passwordIsValid,
        passwordValidationMessage,
        submitLoginForm,
        loginUiState,
        loginErrorMessage,
        checkAzureAdRedirect,
        useConsumerIdentity,
    } = useContext(LoginContext);
    const studioStaffResetPasswordLink = `${webClientsBaseUrl}/PasswordReset?studioid=${subscriberId}`;
    const legacyConsumerLoginLink = useMemo(
        () => getClientsStudioUrl(webClientsBaseUrl, subscriberId),
        [webClientsBaseUrl, subscriberId],
    );
    const [isLoading, setIsLoading] = useState(false);

    const handleMBLogin = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsLoading(true);
        await submitLoginForm();
        setIsLoading(false);
        API.post.trackEvent({ event: 'Sign In || Staff Sign in | Button - Sign in' });
    };

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Staff Sign in' });
    }, []);

    return (
        <PageLayout>
            <div
                data-testid={`${
                    staffAccountType === EStaffAccountType.account ? 'account-staff-login' : 'subscriber-staff-login'
                }`}
            >
                <Card>
                    {(coBranding.logoUrl || coBranding.name) && (
                        <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
                    )}
                    <header>
                        <h1 className={common.pageHeader} data-testid="screen-title">
                            {isStaffLogin
                                ? staffAccountType === EStaffAccountType.account
                                    ? language.login.header.staff.account
                                    : language.login.header.staff.subscriber
                                : language.login.header.consumer}
                        </h1>
                    </header>
                    {loginUiState === ELoginUiState.error && (
                        <section className={common.genericError} data-testid="login-error">
                            <Alert severity="error">{loginErrorMessage}</Alert>
                        </section>
                    )}
                    <form>
                        <main>
                            <TextField
                                // fix for autofill overlapping placeholder
                                // https://github.com/mui/material-ui/issues/14427#issuecomment-1524007603
                                value={username ? username : null}
                                label={isStaffLogin ? language.login.username.staff : language.login.username.consumer}
                                onChange={(event) => setUsername(event.target.value)}
                                error={!usernameIsValid}
                                onBlur={checkAzureAdRedirect}
                                helperText={!usernameIsValid && usernameValidationMessage}
                                margin="normal"
                                autoFocus
                                fullWidth
                                id="username"
                            />

                            <PasswordField
                                // fix for autofill overlapping placeholder
                                // https://github.com/mui/material-ui/issues/14427#issuecomment-1524007603
                                value={password ? password : null}
                                label={language.common.password}
                                error={!passwordIsValid}
                                onChange={(event) => {
                                    setPassword(event.target.value);
                                }}
                                helperText={!passwordIsValid && passwordValidationMessage}
                                id="password"
                            />
                        </main>
                        <section className={styles.forgotPassword}>
                            {staffAccountType === EStaffAccountType.account ? (
                                <Link
                                    component={RouterLink}
                                    to={{
                                        pathname: ERoutes.forgotPasswordStaff,
                                        search: `${EQueryStringParam.customerAccountId}=${customerAccountId}`,
                                    }}
                                >
                                    {language.login.forgotPassword.staffCta}
                                </Link>
                            ) : (
                                <Link
                                    href={studioStaffResetPasswordLink}
                                    onClick={() => {
                                        API.post.trackEvent({
                                            event: 'Sign In || Staff Sign in | Button - Forgot password',
                                        });
                                    }}
                                >
                                    {language.login.forgotPassword.staffCta}
                                </Link>
                            )}
                        </section>
                        <section className={styles.loginCTAs}>
                            <LoadingButton
                                disabled={isLoading}
                                onClick={handleMBLogin}
                                loading={isLoading}
                                type="submit"
                                variant="contained"
                                size="large"
                            >
                                {language.login.loginCTA}
                            </LoadingButton>
                        </section>
                    </form>
                </Card>
                <section className={styles.accountNotStaff} data-testid="consumer-login-link">
                    <p>{language.login.onlyStaff}</p>
                    {useConsumerIdentity ? (
                        <Link href={consumerLoginLink}>{language.login.bookService}</Link>
                    ) : (
                        <Link href={legacyConsumerLoginLink}>{language.login.bookService}</Link>
                    )}
                </section>
            </div>
        </PageLayout>
    );
};

export default LoginStaff;
